import React, { useState, useEffect, useCallback } from 'react';
import Calendar from 'react-calendar';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip,
  TextField // Importado
} from '@mui/material';
import { 
  X, 
  Calendar as CalendarIcon, 
  CheckCircle, 
  AlertTriangle,
  Clock,
  MessageSquare // Ícone para o motivo
} from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { format, parseISO, isSameDay } from 'date-fns';
import 'react-calendar/dist/Calendar.css';
import './CalendarStyles.css';

const PedidosPage = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [myRequests, setMyRequests] = useState([]);
  const [allRequests, setAllRequests] = useState({});
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dayDetailsOpen, setDayDetailsOpen] = useState(false);
  const [requestsForDay, setRequestsForDay] = useState([]);
  const [reason, setReason] = useState(''); // Estado para o motivo

  // ===== VALIDAÇÕES DE DATAS: MÍNIMO E MÁXIMO =====
  const minSelectableDate = new Date();
  minSelectableDate.setHours(0, 0, 0, 0);
  minSelectableDate.setDate(minSelectableDate.getDate() + 10); // 10 dias à frente

  const maxSelectableDate = new Date();
  maxSelectableDate.setHours(0, 0, 0, 0);
  maxSelectableDate.setDate(maxSelectableDate.getDate() + 90); // 3 meses (90 dias) à frente
  // ===============================================

  const boxStyles = {
    p: 2,
    flex: 1,
    minWidth: 300,
    borderRadius: 2,
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    bgcolor: 'background.paper',
    overflow: 'hidden',
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [myRes, allRes, blockedRes] = await Promise.all([
        api.get('/requests/off/me'),
        api.get('/requests/off/all'),
        api.get('/blocked-dates/simple'),
      ]);
      setMyRequests(myRes.data);
      setAllRequests(allRes.data);
      setBlockedDates(blockedRes.data);
    } catch (err) {
      setError('Failed to load requests. Please try again.');
      console.error("Failed to load requests:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateChange = (date) => {
    setSelectedDate(date);
    const dateString = format(date, 'yyyy-MM-dd');
    setRequestsForDay(allRequests[dateString] || []);
    setDayDetailsOpen(true);
  };

  const handleOpenConfirm = () => {
    const dateString = format(selectedDate, 'yyyy-MM-dd');

    // ===== VALIDAÇÃO DE JANELA MÍNIMA (10 dias) =====
    if (selectedDate < minSelectableDate) {
      setError(`Only dates ${format(minSelectableDate, 'MM/dd/yyyy')} or later are allowed (10 days in advance).`);
      setDayDetailsOpen(false);
      return;
    }

    // ===== VALIDAÇÃO DE JANELA MÁXIMA (3 meses / 90 dias) =====
    if (selectedDate > maxSelectableDate) {
      setError(`Time off cannot be requested more than 3 months in advance. Latest allowed date is ${format(maxSelectableDate, 'MM/dd/yyyy')}.`);
      setDayDetailsOpen(false);
      return;
    }

    // Verificações já existentes...
    if (blockedDates.includes(dateString)) {
      setError('This date is blocked for time off requests.');
      setDayDetailsOpen(false);
      return;
    }
    const myRequest = myRequests.find(req => isSameDay(parseISO(req.request_date), selectedDate));
    if (myRequest) {
      setError('You already have a request for this date.');
      setDayDetailsOpen(false);
      return;
    }

    setConfirmOpen(true);
    setDayDetailsOpen(false);
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setReason(''); // Limpa o motivo ao fechar
  };

  const handleRequestOff = async () => {
    setError('');
    setSuccess('');
    setConfirmOpen(false);
    try {
      const dateString = format(selectedDate, 'yyyy-MM-dd');
      // Envia o motivo junto com a requisição
      await api.post('/requests/off', { request_date: dateString, reason: reason });
      setSuccess(`Time off requested for ${format(selectedDate, 'MM/dd/yyyy')}!`);
      setReason(''); // Limpa o motivo após o envio
      fetchData();
    } catch (err) {
      setError(err.response?.data?.msg || 'Failed to submit request.');
      console.error("Error requesting time off:", err);
    }
  };

  const getStatusChip = (status) => {
    let color = 'default';
    if (status === 'approved') color = 'success';
    if (status === 'refused') color = 'error';
    if (status === 'pending') color = 'warning';
    return <Chip label={status} color={color} size="small" />;
  };

  const tileContent = ({ date, view }) => {
    if (view === 'month') {
      const dateString = format(date, 'yyyy-MM-dd');
      const requestsOnDay = allRequests[dateString];
      const isBlocked = blockedDates.includes(dateString);
      const myRequest = myRequests.find(req => isSameDay(parseISO(req.request_date), date));

      return (
        <Box display="flex" justifyContent="center" mt={0.5} flexDirection="column" alignItems="center" sx={{ position: 'relative' }}>
          {isBlocked && (
            <Tooltip title="Blocked Date">
              <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'error.main', mb: 0.2 }} />
            </Tooltip>
          )}
           {myRequest && myRequest.reason && ( // Mostra um ícone se houver motivo
              <Tooltip title={`Reason: ${myRequest.reason}`}>
                <MessageSquare size={10} color="#0288d1" style={{ position: 'absolute', top: 2, right: 2 }}/>
              </Tooltip>
           )}
          {requestsOnDay && requestsOnDay.length > 0 && !isBlocked && (
            (() => {
              const hasApproved = requestsOnDay.some(r => r.status === 'approved');
              const hasPending = requestsOnDay.some(r => r.status === 'pending');

              return hasApproved ? (
                <Tooltip title={`${requestsOnDay.filter(r => r.status === 'approved').length} Approved`}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', mx: 0.2 }} />
                </Tooltip>
              ) : hasPending ? (
                <Tooltip title={`${requestsOnDay.filter(r => r.status === 'pending').length} Pending`}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', mx: 0.2 }} />
                </Tooltip>
              ) : (
                <Tooltip title={`${requestsOnDay.length} Other(s)`}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.500', mx: 0.2 }} />
                </Tooltip>
              );
            })()
          )}
        </Box>
      );
    }
    return null;
  };

  const tileClassName = ({ date, view }) => {
    if (view === 'month') {
      const dateString = format(date, 'yyyy-MM-dd');
      const isBlocked = blockedDates.includes(dateString);
      const myRequest = myRequests.find(req => isSameDay(parseISO(req.request_date), date));
      
      if (isBlocked) {
        return 'blocked-date';
      }
      if (myRequest) {
        return `my-request-${myRequest.status}`;
      }
    }
    return null;
  };

  const tileDisabled = ({ date, view }) => {
    if (view === 'month') {
      const dateString = format(date, 'yyyy-MM-dd');
      // Desabilita datas bloqueadas E datas fora da janela permitida
      const isOutsideRange = date < minSelectableDate || date > maxSelectableDate;
      return blockedDates.includes(dateString) || isOutsideRange;
    }
    return false;
  };

  return (
    <Box p={3} maxWidth={1200} mx="auto">

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
        <Box sx={boxStyles}>
          <Typography variant="h6" gutterBottom>
            Select a Date
          </Typography>
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={300}>
              <CircularProgress />
            </Box>
          ) : (
            <Calendar
              onChange={handleDateChange}
              value={selectedDate}
              tileContent={tileContent}
              tileClassName={tileClassName}
              tileDisabled={tileDisabled}
              minDate={minSelectableDate}
              maxDate={maxSelectableDate}
            />
          )}
          <Box mt={2} p={1} bgcolor="#f9f9f9" borderRadius={1}>
            <Typography variant="caption" display="block" gutterBottom>Legend:</Typography>
            <Box display="flex" alignItems="center" mb={0.5}><MessageSquare size={10} color="#0288d1" style={{ marginRight: '8px' }}/> Your request has a reason</Box>
            <Box display="flex" alignItems="center" mb={0.5}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#e0f7fa', border: '1px solid #4dd0e1', mr: 1 }} /> Your Pending</Box>
            <Box display="flex" alignItems="center" mb={0.5}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#c8e6c9', border: '1px solid #66bb6a', mr: 1 }} /> Your Approved</Box>
            <Box display="flex" alignItems="center" mb={0.5}><Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#ffcdd2', border: '1px solid #ef5350', mr: 1 }} /> Your Refused</Box>
            <Box display="flex" alignItems="center" mb={0.5}><Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: 'error.main', mr: 1 }} /> Blocked Date</Box>
            <Box display="flex" alignItems="center" mb={0.5}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', mr: 1 }} /> Approved (Others)</Box>
            <Box display="flex" alignItems="center" mb={0.5}><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', mr: 1 }} /> Pending (Others)</Box>
            <Box display="flex" alignItems="center"><Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'grey.500', mr: 1 }} /> Others</Box>
          </Box>
        </Box>

        <Box sx={boxStyles}>
          <Typography variant="h6" gutterBottom>
            My Requests
          </Typography>
          {loading ? (
            <CircularProgress />
          ) : myRequests.length === 0 ? (
            <Typography>You haven't requested any time off yet.</Typography>
          ) : (
            <List dense sx={{
              maxHeight: 530,
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#ccc',
                borderRadius: '3px',
              },
            }}>
              {myRequests.map((req) => (
                <ListItem key={req.id} secondaryAction={getStatusChip(req.status)}>
                  <ListItemText
                    primary={format(parseISO(req.request_date), 'MM/dd/yyyy')}
                    secondary={
                      <>
                        <Typography component="span" variant="body2" color="text.secondary" display="block">
                          {`Requested: ${format(parseISO(req.created_at), 'MM/dd/yy HH:mm')}`}
                        </Typography>
                        {req.reason && 
                          <Typography component="span" variant="body2" sx={{ display: 'block', fontStyle: 'italic', color: 'text.secondary' }}>
                            {`Reason: ${req.reason}`}
                          </Typography>
                        }
                      </>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Box>

      {/* Day Details Dialog */}
      <Dialog 
        open={dayDetailsOpen} 
        onClose={() => setDayDetailsOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            fontWeight: 'bold', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarIcon size={24} color="#1976d2" />
            <span>Requests for {format(selectedDate, 'MM/dd/yyyy')}</span>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {(() => {
            const dateString = format(selectedDate, 'yyyy-MM-dd');
            const isBlocked = blockedDates.includes(dateString);
            const myRequest = myRequests.find(req => isSameDay(parseISO(req.request_date), selectedDate));

            if (isBlocked) {
              return (
                <Alert severity="error" sx={{ mb: 2 }}>
                  This date is blocked for time off requests.
                </Alert>
              );
            }

            if (myRequest) {
              return (
                <Alert severity="info" sx={{ mb: 2 }}>
                  You already have a request for this date with status: {myRequest.status}
                </Alert>
              );
            }

            return (
              <>
                {requestsForDay.length === 0 ? (
                  <Box 
                    sx={{ 
                      textAlign: 'center', 
                      py: 4, 
                      bgcolor: '#f9f9f9', 
                      borderRadius: 2,
                      border: '1px dashed #ddd',
                      mb: 2
                    }}
                  >
                    <Typography color="text.secondary">
                      No time off requests for this day.
                    </Typography>
                  </Box>
                ) : (
                  <List dense sx={{ bgcolor: '#f9f9f9', borderRadius: 2, mb: 2 }}>
                    {requestsForDay.map((req, index) => (
                      <ListItem 
                        key={index}
                        sx={{
                          bgcolor: 'white',
                          mb: 1,
                          borderRadius: 1,
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                        }}
                      >
                        <ListItemText
                          primary={`Request from ${req.user_name}`}
                          secondary={`Requested at: ${format(parseISO(req.created_at), 'MM/dd/yyyy HH:mm')} (${req.status})`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                <Typography variant="body2" sx={{ color: '#666' }}>
                  {requestsForDay.length > 0
                    ? `There are already ${requestsForDay.length} request(s) for this day. `
                    : ''
                  }
                  Would you like to request time off for this date?
                </Typography>
              </>
            );
          })()}
        </DialogContent>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button 
            onClick={() => setDayDetailsOpen(false)}
            startIcon={<X size={18} />}
            fullWidth
            sx={{
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
            }}
          >
            Cancel
          </Button>

          {(() => {
            const dateString = format(selectedDate, 'yyyy-MM-dd');
            const isBlocked = blockedDates.includes(dateString);
            const myRequest = myRequests.find(req => isSameDay(parseISO(req.request_date), selectedDate));

            if (!isBlocked && !myRequest) {
              return (
                <Button 
                  onClick={handleOpenConfirm} 
                  variant="contained"
                  startIcon={<Clock size={18} />}
                  fullWidth
                  sx={{
                    fontWeight: 'bold',
                    background: 'linear-gradient(to right, #1976d2, #0d47a1)',
                    color: '#fff',
                    '&:hover': {
                      background: 'linear-gradient(to right, #1565c0, #003c8f)',
                    },
                  }}
                >
                  Request Time Off
                </Button>
              );
            }
            return null;
          })()}
        </Box>
      </Dialog>

      {/* Confirm Dialog with Reason Field */}
      <Dialog 
        open={confirmOpen} 
        onClose={handleCloseConfirm}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle 
          sx={{ 
            fontWeight: 'bold', 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            pb: 1
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircle size={24} color="#1976d2" />
            <span>Confirm Request</span>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            Are you sure you want to request time off for{' '}
            <strong>{format(selectedDate, 'MM/dd/yyyy')}</strong>?
          </Typography>
          
          <TextField
            autoFocus
            margin="dense"
            id="reason"
            label="Reason (obligatory)"
            type="text"
            fullWidth
            variant="outlined"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={3}
          />

          {requestsForDay.length > 0 && (
            <Alert 
              severity="warning" 
              icon={<AlertTriangle size={20} />}
              sx={{ 
                borderRadius: 2,
                mt: 2,
                '& .MuiAlert-icon': {
                  alignItems: 'center'
                }
              }}
            >
              Remember: there are already {requestsForDay.length} request(s) for this day.
              Priority is usually given to the first request.
            </Alert>
          )}
        </DialogContent>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Button 
            onClick={handleCloseConfirm}
            startIcon={<X size={18} />}
            fullWidth
            sx={{
              fontWeight: 'bold',
              backgroundColor: '#f5f5f5',
              color: '#333',
              border: '1px solid #ddd',
              '&:hover': {
                backgroundColor: '#e0e0e0',
              },
            }}
          >
            No, Cancel
          </Button>

          <Button 
            onClick={handleRequestOff} 
            variant="contained"
            startIcon={<CheckCircle size={18} />}
            autoFocus
            fullWidth
            sx={{
              fontWeight: 'bold',
              background: 'linear-gradient(to right, #1976d2, #0d47a1)',
              color: '#fff',
              '&:hover': {
                background: 'linear-gradient(to right, #1565c0, #003c8f)',
              },
            }}
          >
            Yes, Request Time Off
          </Button>
        </Box>
      </Dialog>
    </Box>
  );
}

export default PedidosPage;